import * as React from 'react'
import { useMemo } from 'react'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { classnames } from '@halo/_utils/classnames'
import { Button, Drawer, StatusBadge, Well } from '@pushly/aqe/lib/components'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeploymentUnitOutlined,
    EditOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
    TagFilled,
    UserOutlined,
} from '@ant-design/icons'
import { Divider, Form, Input, Select, Skeleton, Switch, Tooltip } from 'antd'
import { UserDto } from '@halo/dtos/user.dto'
import { UserService } from '@halo/services/user.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { AppState } from '@halo/stores/app.state'
import * as randomstring from 'randomstring'
import { UserTypeId } from '@halo/enums/user-type'
import { fetchWhiteLabels } from '@halo/api/white-labels.api'
import WhiteLabelModel from '@halo/models/white-label.model'
import { StatusType } from '@pushly/aqe/lib/enums'
import { AccessRoleId, ExternalApiAccessRoleIds } from '@halo/enums/access-role.enum'
import FormLayoutRow from '@halo/components/form-layout-row/form-layout-row'
import { AccessRoleMap } from '@halo/models/access-role'
import { PLATFORM_CAN_ACCESS_HALO } from '@halo/constants'
import { ICaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { AbilityAction } from '@halo/enums/ability-action.enum'

interface IUserDetailsEditor {
    user: UserDto
    visible: boolean
    onClose: (e: any) => void
    onUpdated: (account: Partial<UserDto>) => void
    whiteLabelOptions: WhiteLabelModel[] | undefined
}

interface IUserDetailsEditorFormValues {
    name?: string
    email?: string
    userRoleId?: number
    haloAccess?: boolean
}

const UDE_FORM_ITEM_LAYOUT = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 6 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 18 },
    },
}

const computeChangedUserValues = (
    user: UserDto,
    actingUserIsInternal: boolean | undefined,
    formValues: IUserDetailsEditorFormValues,
): Partial<UserDto & { haloAccess: boolean }> => {
    const values: Partial<UserDto & { haloAccess: boolean }> = {}

    if (formValues.name && formValues.name !== user.name) {
        values.name = formValues.name
    }

    if (formValues.email && formValues.email.toLowerCase().trim() !== user.email) {
        values.email = formValues.email.toLowerCase().trim()
    }

    if (user.isInternalUser && actingUserIsInternal) {
        if (formValues.userRoleId !== undefined) {
            values.user_role_id = formValues.userRoleId
        }
    }

    if (!user.isInternalUser && actingUserIsInternal) {
        if (formValues.haloAccess !== undefined) {
            values.haloAccess = formValues.haloAccess
        }
    }

    return values
}

export const UserDetailsEditor: React.FC<IUserDetailsEditor> = (props) => {
    const appState = useMemo(() => Container.get(AppState), [])
    const userService = useMemo(() => Container.get(UserService), [])
    // Preserve singleton resolution side-effect parity with the original class implementation.
    useMemo(() => Container.get(AppService), [])

    const [form] = Form.useForm<IUserDetailsEditorFormValues>()

    const { user, visible, onClose, onUpdated } = props

    // Subscribe to value changes so the wrapping Drawer's disableSubmit recomputes as the user edits.
    Form.useWatch([], form)

    const currentValues = computeChangedUserValues(
        user,
        appState.currentUser?.isInternalUser,
        form.getFieldsValue(),
    )

    const isSelf = appState.currentUser?.id === user?.id
    const canEditDefaultRole = !isSelf && appState.currentUser?.isInternalDevUser && user.isInternalUser

    const handleSubmit = async () => {
        try {
            await form.validateFields()
            const values = computeChangedUserValues(
                user,
                appState.currentUser?.isInternalUser,
                form.getFieldsValue(),
            )

            const { ok, data } = await userService.updateUserById(user.id, values, {
                showLoadingScreen: true,
            })

            if (ok) {
                onUpdated(data)
                onClose(null)
                simpleNotification('success', `User, ${user.name}, has been successfully updated.`)
            }
        } catch (err) {
            // v6 validateFields rejects with { errorFields, values, outOfDate };
            // aqe's simpleFormErrorNotification handles both legacy and v6 shapes.
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            className={classnames('user-details-editor')}
            title="Edit User"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleSubmit}
            disableSubmit={Object.keys(currentValues).length === 0}
            destroyOnClose={true}
            onClose={onClose}
            open={visible}
        >
            <Form
                form={form}
                {...UDE_FORM_ITEM_LAYOUT}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Form.Item
                    label="Name"
                    name="name"
                    initialValue={user.name}
                    rules={[
                        {
                            required: true,
                            message: 'Name is a required field.',
                        },
                        {
                            pattern: new RegExp('^(?!.*<.*>).*'),
                            message: 'Name cannot contain HTML tags.',
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Tooltip
                    title={
                        !appState.currentUser?.isInternalUser
                            ? 'Please contact your account manager to request email address changes.'
                            : ''
                    }
                >
                    <Form.Item
                        label="Email"
                        name="email"
                        initialValue={user.email}
                        rules={[
                            {
                                required: true,
                                message: 'Email is a required field.',
                            },
                        ]}
                    >
                        <Input disabled={!appState.currentUser?.isInternalUser} />
                    </Form.Item>
                </Tooltip>
                {canEditDefaultRole && (
                    <Form.Item
                        label="Default User Role"
                        name="userRoleId"
                        initialValue={user.user_role_id}
                    >
                        <Select>
                            {appState.currentUser?.isInternalDevUser && [
                                <Select.Option key="dev" value={AccessRoleId.INTERNAL_DEVELOPER}>
                                    Developer
                                </Select.Option>,
                                <Select.Option key="api" value={AccessRoleId.INTERNAL_API_USER}>
                                    API User
                                </Select.Option>,
                            ]}

                            <Select.Option value={AccessRoleId.INTERNAL_ACCOUNT_MANAGER_LEAD}>
                                Account Manager Lead
                            </Select.Option>
                            <Select.Option value={AccessRoleId.INTERNAL_ACCOUNT_MANAGER}>
                                Account Manager
                            </Select.Option>
                            <Select.Option value={AccessRoleId.INTERNAL_SALES_ASSOCIATE}>
                                Sales Associate
                            </Select.Option>
                            <Select.Option value={AccessRoleId.INTERNAL_USER}>
                                User <b>(Default)</b>
                            </Select.Option>
                            <Select.Option value={AccessRoleId.INTERNAL_READ_ONLY}>Read-Only</Select.Option>

                            {appState.currentUser?.isInternalDevUser && (
                                <Select.Option value={AccessRoleId.INTERNAL_NO_ACCESS}>No Access</Select.Option>
                            )}
                        </Select>
                    </Form.Item>
                )}

                {!user.isInternalUser && appState.currentUser?.isInternalUser && (
                    <React.Fragment>
                        <Divider />

                        <Form.Item
                            label="Halo Access"
                            name="haloAccess"
                            initialValue={user.flags?.includes(PLATFORM_CAN_ACCESS_HALO)}
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </React.Fragment>
                )}
            </Form>
        </Drawer>
    )
}

interface IUserDetails {
    user: UserDto | any
    accountId: number | undefined
    caslUser: ICaslSubject<SubjectEntity.USER>
    loading: boolean
    onUpdated: (account: Partial<UserDto>) => void
}

interface IUserDetailsState {
    showDrawer: boolean
    drawerKey?: string
    loadingWhiteLabels: boolean
    whiteLabels?: WhiteLabelModel[]
}

export class UserDetailsWell extends React.Component<IUserDetails, IUserDetailsState> {
    protected appService: AppService
    protected appState: AppState
    protected userService: UserService

    public constructor(props: IUserDetails) {
        super(props)

        this.appService = Container.get(AppService)
        this.appState = Container.get(AppState)
        this.userService = Container.get(UserService)

        this.state = {
            showDrawer: false,
            loadingWhiteLabels: true,
        }
    }

    public componentDidMount() {
        this.loadWhiteLabels()
    }

    public componentDidUpdate(prevProps: Readonly<IUserDetails>) {
        if (!prevProps.user && !!this.props.user) {
            this.loadWhiteLabels()
        }
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Details"
                hideFooter={true}
                actions={
                    this.appState.abilityStore.can(AbilityAction.UPDATE, this.props.caslUser)
                        ? this.buildActions()
                        : undefined
                }
            >
                <Skeleton loading={this.props.loading || this.state.loadingWhiteLabels} active={true} title={false}>
                    {!this.props.loading && !this.state.loadingWhiteLabels && (
                        <>
                            {this.renderDetails()}

                            <UserDetailsEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                user={this.props.user}
                                onClose={this.handleDrawerClose}
                                onUpdated={this.props.onUpdated}
                                whiteLabelOptions={this.state.whiteLabels}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderDetails(): React.ReactNode {
        const user = this.props.user

        let userTypeText = '--'
        let userTypeIcon = <UserOutlined />

        if (
            (user.user_type_id === UserTypeId.INTERNAL && user.user_role_id === AccessRoleId.INTERNAL_API_USER) ||
            user.user_type_id === UserTypeId.INTERNAL_API
        ) {
            // legacy api user type
            userTypeText = 'Internal API User'
            userTypeIcon = <DeploymentUnitOutlined />
        } else if (user.user_type_id === UserTypeId.INTERNAL) {
            userTypeText = 'Internal Platform User'
        } else if (
            (user.user_type_id === UserTypeId.EXTERNAL && ExternalApiAccessRoleIds.includes(user.user_role_id)) ||
            user.user_type_id === UserTypeId.EXTERNAL_API
        ) {
            // legacy api user type
            switch (user.user_role_id) {
                case AccessRoleId.EXTERNAL_API_USER:
                    userTypeText = 'External Org API User'
                    break
                case AccessRoleId.EXTERNAL_CMS_API_USER:
                    userTypeText = 'External CMS User'
                    break
                case AccessRoleId.EXTERNAL_DOMAIN_API_USER:
                    userTypeText = 'External Domain API User'
                    break
            }
            userTypeIcon = <DeploymentUnitOutlined />
        } else if (user.user_type_id === UserTypeId.EXTERNAL) {
            userTypeText = 'External Platform User'
        }

        return (
            <Well.Section title="User Information">
                <FormLayoutRow label="Name" value={user.name} />
                <FormLayoutRow label="Email" value={user.email} />

                <FormLayoutRow
                    label="Type"
                    valueClassName="with-icon"
                    value={
                        <React.Fragment>
                            {userTypeIcon}
                            <span>{userTypeText}</span>
                        </React.Fragment>
                    }
                />

                <FormLayoutRow
                    label="Current Status"
                    value={<StatusBadge type="user" status={user.status} expanded={true} />}
                />

                {user.isInternalUser && this.appState.currentUser?.isInternalUser && (
                    <FormLayoutRow
                        label="Default Platform User Role"
                        value={AccessRoleMap.get(user.user_role_id)?.label}
                    />
                )}

                {!user.isInternalUser && this.appState.currentUser?.isInternalUser && (
                    <React.Fragment>
                        <Divider />
                        <FormLayoutRow
                            label="Halo Access"
                            value={
                                <StatusBadge
                                    type="access"
                                    status={user.flags?.includes(PLATFORM_CAN_ACCESS_HALO) ? 'GRANTED' : 'DENIED'}
                                    expanded={true}
                                />
                            }
                            subClassName="info-icon-flex"
                            sub={
                                <React.Fragment>
                                    <InfoCircleOutlined />
                                    <span>
                                        External users with Halo access will be presented with <b>restricted</b> views
                                        consisting of only
                                        <br />
                                        Organizations, Domains, and Users for the organizations they have admin access
                                        on.
                                    </span>
                                </React.Fragment>
                            }
                        />
                    </React.Fragment>
                )}
            </Well.Section>
        )
    }

    protected buildActions(): React.ReactNode {
        const user = this.props.user
        const actions = []

        if (user) {
            const isSelf = this.appState.currentUser?.id === user.id
            const userDisabled = user.status_id === StatusType.DISABLED
            const showReInvite =
                this.props.accountId && !isSelf && !user.isInternalUser && user.status_id === StatusType.INVITED
            const showResetPassword =
                this.props.accountId &&
                !isSelf &&
                !user.isInternalUser &&
                user.status_id !== StatusType.INVITED &&
                user.is_db_user &&
                !user.isApiUser
            const showDisableUser = !isSelf && (this.appState.currentUser!.isInternalUser || !user.isInternalUser)

            if (!isSelf) {
                if (userDisabled) {
                    actions.push(
                        <span key="activate">
                            <Button size="small" onClick={this.activateUser} disabled={this.props.loading}>
                                <CheckCircleOutlined />
                                <span>Activate</span>
                            </Button>
                        </span>,
                    )
                } else {
                    if (showReInvite) {
                        actions.push(
                            <span key="reinvite">
                                <Button size="small" onClick={this.reInviteUser} disabled={this.props.loading}>
                                    <MailOutlined />
                                    <span>Re-Invite</span>
                                </Button>
                            </span>,
                        )
                    }
                    if (showResetPassword) {
                        actions.push(
                            <span key="resetpwd">
                                <Button size="small" onClick={this.forcePasswordReset} disabled={this.props.loading}>
                                    <LockOutlined />
                                    <span>Password Reset</span>
                                </Button>
                            </span>,
                        )
                    }
                    if (showDisableUser) {
                        actions.push(
                            <span key="deactivate">
                                <Button size="small" onClick={this.deactivateUser} disabled={this.props.loading}>
                                    <CloseCircleOutlined />
                                    <span>Deactivate</span>
                                </Button>
                            </span>,
                        )
                    }
                }
            }
        }

        if (actions.length > 0) {
            actions.push(<span key="spacer1" className="actions-spacer" />)
        }

        actions.push(
            <span key="edit">
                <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            </span>,
        )

        return actions
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected deactivateUser = async () => {
        const response = await this.userService.updateUserById(
            this.props.user.id,
            { status: 'DISABLED' },
            {
                showLoadingScreen: true,
                cancellationKey: `disable-user-${this.props.user.id}`,
            },
        )

        if (response.ok) {
            this.props.onUpdated(response.data)
        }
    }

    protected activateUser = async () => {
        const response = await this.userService.updateUserById(
            this.props.user.id,
            { status: 'ACTIVE' },
            {
                showLoadingScreen: true,
                cancellationKey: `enable-user-${this.props.user.id}`,
            },
        )

        if (response.ok) {
            this.props.onUpdated(response.data)
        }
    }

    protected reInviteUser = async (): Promise<void> => {
        const response = await this.userService.triggerInviteForUserId(
            this.props.user.id,
            this.props.accountId,
            {
                showLoadingScreen: true,
                cancellationKey: `reinvite-user-${this.props.user.id}`,
            },
            this.appState,
        )

        if (response.ok) {
            this.props.onUpdated({
                status_id: response.data.status_id,
                status: response.data.status,
            })
        }
    }

    protected forcePasswordReset = async (): Promise<void> => {
        const response = await this.userService.triggerPasswordResetForUserId(
            this.props.user.id,
            this.props.accountId,
            {
                showLoadingScreen: true,
                cancellationKey: `resetpwd-user-${this.props.user.id}`,
            },
            this.appState,
        )

        if (response.ok) {
            this.props.onUpdated({
                status_id: response.data.status_id,
                status: response.data.status,
            })
        }
    }

    protected async loadWhiteLabels(): Promise<void> {
        const user = this.props.user

        const update: Partial<IUserDetailsState> = { loadingWhiteLabels: true }
        this.setState(update as IUserDetailsState)

        if (user?.user_type === 'INTERNAL') {
            const res = await fetchWhiteLabels()

            if (res.ok) {
                update.whiteLabels = res.data?.map(WhiteLabelModel.build)
            }
        }

        update.loadingWhiteLabels = false
        this.setState(update as IUserDetailsState)
    }
}
